<template>
	<landing-page-template>
		<v-container class="my-0 py-0">
			<!-- Hero Section -->
			<div class="py-8 py-md-16">
				<v-row class="d-flex justify-center">
					<v-col cols="12" class="text-center">
						<v-card-subtitle
							:class="isMobile ? 'v-typography--title1' : 'v-typography--title2'"
							class="accent--text title-with-line pa-0"
							tag="h1"
						>
							{{ $t("$landing.$tracking.$detailPage.hero_title") }}
						</v-card-subtitle>
					</v-col>
				</v-row>
				<v-row class="d-flex justify-center mt-4">
					<v-col cols="12" md="10" class="text-center">
						<v-card-title
							:class="isMobile ? 'v-typography--title' : 'v-typography--title3'"
							class="pa-1 justify-center"
							tag="h2"
						>
							{{ $t("$landing.$tracking.$detailPage.hero_subtitle") }}
						</v-card-title>
					</v-col>
				</v-row>
				<v-row class="d-flex justify-center mt-2">
					<v-col cols="12" md="8" class="text-center">
						<v-card-text :class="isMobile ? 'v-typography--body1' : 'v-typography--paragraph'" class="pa-2" tag="p">
							{{ $t("$landing.$tracking.$detailPage.hero_description") }}
						</v-card-text>
					</v-col>
				</v-row>
			</div>

			<!-- Main Tracking Section -->
			<landing-tracking
				id="tracking"
				class="py-8 py-md-16"
				:isMobile="isMobile"
				:isTablet="isTablet"
				:useDetailBenefits="true"
			/>

			<!-- Features Grid -->
			<div class="py-8 py-md-16">
				<v-row class="d-flex justify-center mb-8">
					<v-col cols="12" class="text-center">
						<v-card-subtitle
							:class="isMobile ? 'v-typography--title1' : 'v-typography--title2'"
							class="accent--text title-with-line pa-0"
							tag="h2"
						>
							{{ $t("$landing.$tracking.$detailPage.features_title") }}
						</v-card-subtitle>
					</v-col>
				</v-row>

				<v-row class="justify-center">
					<v-col v-for="feature in features" :key="feature.key" cols="12" sm="6" md="4">
						<v-card class="ant-card pa-6 h-100" outlined elevation="0">
							<v-icon size="40" color="accent" class="mb-4">{{ feature.icon }}</v-icon>
							<v-card-title class="v-typography--title1 pa-0 mb-3" tag="h3">
								{{ $t(feature.title) }}
							</v-card-title>
							<v-card-text :class="isMobile ? 'v-typography--body1' : 'v-typography--paragraph'" class="pa-0" tag="p">
								{{ $t(feature.desc) }}
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</div>

			<!-- How It Works Section -->
			<div class="py-8 py-md-16">
				<v-row class="d-flex justify-center mb-8">
					<v-col cols="12" class="text-center">
						<v-card-subtitle
							:class="isMobile ? 'v-typography--title1' : 'v-typography--title2'"
							class="accent--text title-with-line pa-0"
							tag="h2"
						>
							{{ $t("$landing.$tracking.$detailPage.workflow_title") }}
						</v-card-subtitle>
					</v-col>
				</v-row>

				<v-row class="justify-center">
					<v-col v-for="(step, index) in workflowSteps" :key="index" cols="12" sm="6" md="3" class="text-center">
						<div class="step-number mb-4 mx-auto">{{ index + 1 }}</div>
						<v-card-title class="v-typography--title1 pa-0 mb-3 justify-center" tag="h3">
							{{ $t(step.title) }}
						</v-card-title>
						<v-card-text :class="isMobile ? 'v-typography--body1' : 'v-typography--paragraph'" class="pa-2" tag="p">
							{{ $t(step.desc) }}
						</v-card-text>
					</v-col>
				</v-row>
			</div>

			<!-- CTA Section -->
			<div class="py-8 py-md-16">
				<v-row class="d-flex justify-center">
					<v-col cols="12" md="8" class="text-center">
						<v-card-title
							:class="isMobile ? 'v-typography--title' : 'v-typography--title3'"
							class="pa-1 mb-4 justify-center"
							tag="h2"
						>
							{{ $t("$landing.$tracking.$detailPage.cta_title") }}
						</v-card-title>
						<v-btn x-large color="secondary" class="mt-4" @click="goToSignUp">
							<span :class="isTablet ? 'v-typography--paragraph' : ''">
								{{ $t("$landing.$tracking.$detailPage.cta_button") }}
							</span>
						</v-btn>
						<v-card-text class="mt-4 v-typography--body1">
							{{ $t("$landing.$tracking.$detailPage.cta_subtitle") }}
						</v-card-text>
					</v-col>
				</v-row>
			</div>
		</v-container>
	</landing-page-template>
</template>

<script>
import uiMixin from "../../../../store/interfaces/ui.mixin";
import LandingPageTemplate from "../../../templates/LandingPageTemplate.vue";
import LandingTracking from "../partial/LandingTracking.vue";

export default {
	components: {
		LandingPageTemplate,
		LandingTracking,
	},
	mixins: [uiMixin],
	data() {
		return {
			features: [
				{
					key: 1,
					icon: "icon-location",
					title: "$landing.$tracking.$detailPage.feature_1_title",
					desc: "$landing.$tracking.$detailPage.feature_1_desc",
				},
				{
					key: 2,
					icon: "icon-bell",
					title: "$landing.$tracking.$detailPage.feature_2_title",
					desc: "$landing.$tracking.$detailPage.feature_2_desc",
				},
				{
					key: 3,
					icon: "icon-clock",
					title: "$landing.$tracking.$detailPage.feature_3_title",
					desc: "$landing.$tracking.$detailPage.feature_3_desc",
				},
				{
					key: 4,
					icon: "icon-user",
					title: "$landing.$tracking.$detailPage.feature_4_title",
					desc: "$landing.$tracking.$detailPage.feature_4_desc",
				},
				{
					key: 5,
					icon: "icon-document",
					title: "$landing.$tracking.$detailPage.feature_5_title",
					desc: "$landing.$tracking.$detailPage.feature_5_desc",
				},
				{
					key: 6,
					icon: "icon-notification",
					title: "$landing.$tracking.$detailPage.feature_6_title",
					desc: "$landing.$tracking.$detailPage.feature_6_desc",
				},
			],
			workflowSteps: [
				{
					title: "$landing.$tracking.$detailPage.step_1_title",
					desc: "$landing.$tracking.$detailPage.step_1_desc",
				},
				{
					title: "$landing.$tracking.$detailPage.step_2_title",
					desc: "$landing.$tracking.$detailPage.step_2_desc",
				},
				{
					title: "$landing.$tracking.$detailPage.step_3_title",
					desc: "$landing.$tracking.$detailPage.step_3_desc",
				},
				{
					title: "$landing.$tracking.$detailPage.step_4_title",
					desc: "$landing.$tracking.$detailPage.step_4_desc",
				},
			],
		};
	},
	methods: {
		goToSignUp() {
			this.$router.push({ name: "signup" });
		},
	},
};
</script>

<style scoped>
.title-with-line {
	position: relative;
	display: inline-block;
}

.title-with-line::after {
	content: "";
	position: absolute;
	left: 0;
	bottom: 0;
	width: 100%;
	height: 2px;
	background-color: currentColor;
	transform: scaleX(0.1);
	transform-origin: left;
}

.check-img {
	width: 17px;
	height: 17px;
	flex-shrink: 0;
}

.step-number {
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background-color: #b71c1c;
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	font-weight: bold;
}

.h-100 {
	height: 100%;
}

@media (min-width: 1904px) {
	.container {
		max-width: 1185px !important;
	}
}

@media (max-width: 960px) {
	.title-with-line::after {
		width: 40px;
		height: 2px;
	}
}

@media (max-width: 600px) {
	.title-with-line::after {
		height: 0px;
	}
}
</style>
